import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/api/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { UserPlus, Shield, Trash2, Edit, Power, PowerOff } from 'lucide-react';
import { toast } from 'sonner';

const ModeratorsManagement = () => {
  const [moderators, setModerators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedModerator, setSelectedModerator] = useState(null);
  
  // Create form
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: ''
  });
  
  // Edit form
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    password: ''
  });
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchModerators();
  }, []);

  const fetchModerators = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getModerators();
      setModerators(response.data.moderators || []);
    } catch (error) {
      toast.error('Failed to fetch moderators');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateModerator = async (e) => {
    e.preventDefault();
    
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await adminAPI.createModerator(createForm);
      
      // Show success message with email status
      if (response.data.email_status === 'sent') {
        toast.success('Moderator created successfully! Welcome email sent.');
      } else {
        toast.success('Moderator created successfully! (Welcome email could not be sent)');
      }
      
      setShowCreateModal(false);
      setCreateForm({ name: '', email: '', phone: '', password: '' });
      fetchModerators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create moderator');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTogglePermission = async (moderatorId, permission, currentValue) => {
    try {
      const response = await adminAPI.updateModeratorPermissions(moderatorId, {
        [permission]: !currentValue
      });
      toast.success('Permission updated successfully');
      fetchModerators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permission');
    }
  };

  const handleToggleStatus = async (moderatorId, currentStatus) => {
    try {
      const response = await adminAPI.toggleModeratorStatus(moderatorId);
      toast.success(response.data.message);
      fetchModerators();
    } catch (error) {
      toast.error('Failed to update moderator status');
    }
  };

  const handleDeleteModerator = async (moderatorId, moderatorName) => {
    if (!window.confirm(`Are you sure you want to delete moderator "${moderatorName}"?`)) {
      return;
    }
    
    try {
      await adminAPI.deleteModerator(moderatorId);
      toast.success('Moderator deleted successfully');
      fetchModerators();
    } catch (error) {
      toast.error('Failed to delete moderator');
    }
  };

  const handleEditModerator = async (e) => {
    e.preventDefault();
    
    if (!editForm.name) {
      toast.error('Name is required');
      return;
    }
    
    setSubmitting(true);
    try {
      await adminAPI.updateModerator(selectedModerator.id, editForm);
      toast.success('Moderator updated successfully!');
      setShowEditModal(false);
      setSelectedModerator(null);
      setEditForm({ name: '', phone: '', password: '' });
      fetchModerators();
    } catch (error) {
      toast.error('Failed to update moderator');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (moderator) => {
    setSelectedModerator(moderator);
    setEditForm({
      name: moderator.name,
      phone: moderator.phone || '',
      password: '' // Empty by default
    });
    setShowEditModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Moderators Management</h2>
          <p className="text-sm text-gray-500 mt-1">Manage moderator accounts and permissions</p>
        </div>
        
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
              <UserPlus className="h-4 w-4 mr-2" />
              Add Moderator
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Moderator</DialogTitle>
              <DialogDescription>
                Add a new moderator to manage exam bookings
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleCreateModerator} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder="Enter full name"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  placeholder="moderator@example.com"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Phone (Optional)</Label>
                <Input
                  id="phone"
                  value={createForm.phone}
                  onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                  placeholder="01XXXXXXXXX"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  placeholder="Enter password"
                  required
                />
              </div>
              
              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Moderator'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Moderators List */}
      <Card>
        <CardHeader>
          <CardTitle>Active Moderators</CardTitle>
          <CardDescription>
            Total: {moderators.length} moderator{moderators.length !== 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Loading moderators...</p>
            </div>
          ) : moderators.length === 0 ? (
            <div className="text-center py-8">
              <Shield className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">No moderators found</p>
              <p className="text-sm text-gray-400 mt-1">Create your first moderator to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {moderators.map((moderator) => (
                <div
                  key={moderator.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                      {moderator.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <p className="font-semibold text-gray-900">{moderator.name}</p>
                        <Badge variant={moderator.is_active ? 'default' : 'secondary'}>
                          {moderator.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500">{moderator.email}</p>
                      {moderator.phone && (
                        <p className="text-xs text-gray-400">{moderator.phone}</p>
                      )}
                      
                      {/* Permissions Section */}
                      <div className="mt-2 flex items-center gap-4 flex-wrap">
                        <div className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            id={`credit-edit-${moderator.id}`}
                            checked={moderator.can_edit_student_credits || false}
                            onChange={() => handleTogglePermission(
                              moderator.id, 
                              'can_edit_student_credits', 
                              moderator.can_edit_student_credits || false
                            )}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <label htmlFor={`credit-edit-${moderator.id}`} className="text-gray-600 cursor-pointer">
                            Can Edit Credits
                          </label>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            id={`batch-manage-${moderator.id}`}
                            checked={moderator.can_manage_batches || false}
                            onChange={() => handleTogglePermission(
                              moderator.id, 
                              'can_manage_batches', 
                              moderator.can_manage_batches || false
                            )}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <label htmlFor={`batch-manage-${moderator.id}`} className="text-gray-600 cursor-pointer">
                            Can Manage Batches
                          </label>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            id={`batch-validity-${moderator.id}`}
                            checked={moderator.can_manage_batch_validity || false}
                            onChange={() => handleTogglePermission(
                              moderator.id, 
                              'can_manage_batch_validity', 
                              moderator.can_manage_batch_validity || false
                            )}
                            className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <label htmlFor={`batch-validity-${moderator.id}`} className="text-gray-600 cursor-pointer">
                            Can Edit Validity
                          </label>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            id={`notice-manage-${moderator.id}`}
                            checked={moderator.can_manage_notices || false}
                            onChange={() => handleTogglePermission(
                              moderator.id, 
                              'can_manage_notices', 
                              moderator.can_manage_notices || false
                            )}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <label htmlFor={`notice-manage-${moderator.id}`} className="text-gray-600 cursor-pointer">
                            Can Manage Notices
                          </label>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            id={`external-bookings-${moderator.id}`}
                            data-testid={`toggle-external-bookings-${moderator.id}`}
                            checked={moderator.can_manage_external_bookings || false}
                            onChange={() => handleTogglePermission(
                              moderator.id,
                              'can_manage_external_bookings',
                              moderator.can_manage_external_bookings || false
                            )}
                            className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                          />
                          <label htmlFor={`external-bookings-${moderator.id}`} className="text-gray-600 cursor-pointer">
                            Can Approve External Bookings
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditModal(moderator)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    
                    <Button
                      size="sm"
                      variant={moderator.is_active ? 'outline' : 'default'}
                      onClick={() => handleToggleStatus(moderator.id, moderator.is_active)}
                    >
                      {moderator.is_active ? (
                        <PowerOff className="h-4 w-4" />
                      ) : (
                        <Power className="h-4 w-4" />
                      )}
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteModerator(moderator.id, moderator.name)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Moderator</DialogTitle>
            <DialogDescription>
              Update moderator information
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleEditModerator} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Full Name *</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Phone</Label>
              <Input
                id="edit-phone"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                placeholder="01XXXXXXXXX"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-password">New Password (Optional)</Label>
              <Input
                id="edit-password"
                type="password"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                placeholder="Leave empty to keep current password"
              />
              <p className="text-xs text-gray-500">Only fill this if you want to change the password</p>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Updating...' : 'Update Moderator'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="space-y-1">
              <p className="font-semibold text-blue-900">Moderator Permissions</p>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>✓ Create and manage exam schedules</li>
                <li>✓ Manual booking for students</li>
                <li>✓ View and cancel bookings</li>
                <li>✓ Create new students</li>
                <li>✓ View student information</li>
              </ul>
              <p className="text-sm text-blue-700 mt-2">
                <strong>Optional (with permission):</strong> Edit credits, Manage batches, Edit batch validity
              </p>
              <p className="text-sm text-red-600 mt-1">
                <strong>Restricted:</strong> Cannot delete students
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ModeratorsManagement;
